import { Button, Icon, Input, Spacer, TagInput, ThirdTitle, Title } from '@dspworkplace/ui'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAuth } from '../../../Auth';
import { Label, Wrapper } from "../../../components/UI";
import { SketchPicker } from 'react-color';
import { calculateContrast } from '../../../utils';
import styled from 'styled-components'
import { getCompanyId, getUserId } from '../../../Utilities';
import { TagInputContainer } from '../../../AppTwilioConversation/styles';
import { LoadingView } from '../../../components/Loading';
import { toast } from '../../../components/Toasts';
import { AccessCheck, IsGranted } from '../../../security';
import { ACCESS_ROLES, useOnboarding } from '../../Onboarding/context';

function OnboardingSettings() {

    const { register, errors, handleSubmit, reset, control, setValue, getValues } = useForm()
    const { api } = useAuth();
    const [loading, setLoading] = useState(false)
    const [users, setUsers] = useState([])

    const { settings, hasAccess, loading: settingLoading, setSettings } = useOnboarding()
    const values = getValues();

    const currentUserId = getUserId();

    useEffect(() => {
        getUsers()
    }, [])

    useEffect(() => {
        reset({ ...settings })
    }, [settings])

    async function getUsers() {
        await setLoading(true)
        await api.post('/api/bot/twilio/employees', {
            companyId: getCompanyId(),
            notEqualRole: 'ROLE_DELIVERY_ASSOCIATE'
        }).then(res => {
            if (res.data && res.data?.users) {
                const data = res.data?.users;
                setUsers(data)
            }
        }).catch(error => {
            setLoading(false)
        }).finally(() => {
            setLoading(false)
        })
    }

    const saveSettings = async (data) => {
        if (!hasAccess) {
            toast({ type: 'warning', title: 'You do not have access to this feature.' })
            return
        }

        await setLoading(true)
        const payload = {
            ...data,
            linkExpirationDays: Number(data?.linkExpirationDays)
        }
        api.put("/api/onboarding-setting", payload)
            .then(res => {
                if (res.data.data) {
                    setSettings(res.data.data)
                }
            }).catch(error => {
                setLoading(false)
            }).finally(() => {
                setLoading(false)
            })
    }

    return (
        <Wrapper position="relative">
            {(loading || settingLoading) &&
                <LoadingView containerHeight='100%' imgHeight='40px' overlay={true} imgMarginTop='250px' />
            }
            {hasAccess ?
                <>
                    <Spacer bottom={5} />
                    <Title>Onboarding Settings</Title>
                    <Spacer bottom={5} />
                    <Form onSubmit={handleSubmit(saveSettings)}>
                        <ColourRow>
                            <input type="hidden" name="primaryTextColor" ref={register} />
                            <input type="hidden" name="secondaryTextColor" ref={register} />
                            <div>
                                <Label>Primary Color</Label>
                                <Controller
                                    name='primaryColor'
                                    control={control}
                                    as={SketchPicker}
                                    valueName='color'
                                    defaultValue={settings?.primaryColor}
                                    onChange={([color]) => {
                                        const hex = color.hex
                                        const isBlack = calculateContrast(hex, '#333333')
                                        setValue('primaryTextColor', isBlack ? '#333333' : '#ffffff')
                                        return hex
                                    }}
                                />
                            </div>

                            <div>
                                <Label>Secondary Color</Label>
                                <Controller
                                    name='secondaryColor'
                                    control={control}
                                    as={SketchPicker}
                                    valueName='color'
                                    defaultValue={settings?.secondaryColor}
                                    onChange={([color]) => {
                                        const hex = color.hex
                                        const isBlack = calculateContrast(hex, '#333333')
                                        setValue('secondaryTextColor', isBlack ? '#333333' : '#ffffff')
                                        return hex
                                    }}
                                />
                            </div>
                        </ColourRow>

                        <Input
                            name="linkExpirationDays"
                            label="Applicant Link Expiration (Days)"
                            type="number"
                            ref={register({ required: 'Applicant Link Expiration is required' })}
                            error={errors.linkExpirationDays && errors.linkExpirationDays.message}
                            valid={values?.linkExpirationDays && !errors.linkExpirationDays}
                        />

                        <InfoCard>
                            <InfoRow>
                                <Icon.Info size="20px" color="#516F90" />
                                <ThirdTitle>Note</ThirdTitle>
                            </InfoRow>
                            <InfoText>
                                By default, the following roles will have admin-level access in the Onboarding section:<br />
                                • Owner<br />
                                These roles will automatically receive full access to manage and use all onboarding-related features and settings.
                            </InfoText>
                        </InfoCard>

                        <TagInputContainer width={"458px"} templateRows={"auto"} >
                            <TagInput
                                name={"hasAccessPermission"}
                                ref={register({
                                    validate: (value) => {
                                        const ids = (value ?? []).map(String)
                                        if(ids.length === 0){
                                            return 'At least one selected user for onboarding admin access.';
                                        }
                                    }
                                })}
                                label="Onboarding Admin Access"
                                options={users?.map(i => ({ name: i.friendlyName, value: i.userId }))}
                                visibleOptionsQty={4}
                                disabled={!hasAccess}
                                defaultValues={settings?.hasAccessPermission ?? []}
                                error={errors.hasAccessPermission && errors.hasAccessPermission.message}
                            />
                        </TagInputContainer>

                        <Button type='primary' disabled={loading || !hasAccess} style={{ width: 'fit-content' }}>Save</Button>
                    </Form>
                </>
                :
                <IsGranted feature={AccessCheck.features.ONBOARDING_SETTING} renderWarning={true} />
            }
        </Wrapper>
    )
}

export default OnboardingSettings

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 100px;
`

const ColourRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
`

const InfoCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    background-color: #eaf0f6;
    border: 1px solid #7c98b6;
    border-radius: 2px;
`

const InfoRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
`

const InfoText = styled.span`
    font-size: 12px;
    color: #516F90;
    font-weight: 500;
`